// TaskDetailModal — full read-only view of a task's details (portaled to body).
// Opened from a task card so long descriptions / all fields are fully visible.

import React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import "../../css/tasks/taskdetail.css";

const STATUS_LABELS = {
  pending: "Pending",
  inProgress: "In Progress",
  completed: "Completed",
  overdue: "Overdue",
};

const statusClass = (s) => {
  switch (s) {
    case "inProgress": return "td-status-inprogress";
    case "completed":  return "td-status-completed";
    case "overdue":    return "td-status-overdue";
    default:           return "td-status-pending";
  }
};

const priorityClass = (p) => {
  switch (p) {
    case "urgent": return "td-prio-urgent";
    case "high":   return "td-prio-high";
    case "low":    return "td-prio-low";
    default:       return "td-prio-medium";
  }
};

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (e) {
    return String(d);
  }
};

const person = (p) => (!p ? "—" : (p.Name || p.name || p.email || "—"));

export default function TaskDetailModal({ task, onClose }) {
  if (!task) return null;

  const typeLabel =
    task.type === "other" ? (task.customType || "Other") : task.type;

  return createPortal(
    <div className="td-overlay" onClick={onClose}>
      <div className="td-modal" onClick={(e) => e.stopPropagation()}>
        <div className="td-header">
          <h3 className="td-title">{task.title || task.taskTitle}</h3>
          <button className="td-close" type="button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="td-body">
          {/* Badges */}
          <div className="td-badges">
            <span className={"td-badge " + priorityClass(task.priority)}>
              {task.priority || "medium"} priority
            </span>
            <span className={"td-badge " + statusClass(task.status)}>
              {STATUS_LABELS[task.status] || task.status}
            </span>
            {typeLabel && (
              <span className="td-badge td-type">{typeLabel}</span>
            )}
          </div>

          {/* Description (full, no truncation) */}
          <div className="td-section">
            <label>Description</label>
            <p className="td-desc">
              {task.description ? task.description : "No description provided."}
            </p>
          </div>

          {/* Detail grid */}
          <div className="td-grid">
            <div className="td-field">
              <label>Target Date</label>
              <span>{task.dueDate ? fmtDate(task.dueDate) : "—"}</span>
            </div>
            <div className="td-field">
              <label>Assigned On</label>
              <span>{fmtDate(task.createdAt)}</span>
            </div>
            <div className="td-field">
              <label>Assigned To</label>
              <span>{person(task.assignedTo)}</span>
            </div>
            <div className="td-field">
              <label>Assigned By</label>
              <span>{person(task.assignedBy)}</span>
            </div>
            {task.startedDate && (
              <div className="td-field">
                <label>Started On</label>
                <span>{fmtDate(task.startedDate)}</span>
              </div>
            )}
            {task.completedDate && (
              <div className="td-field">
                <label>Completed On</label>
                <span>{fmtDate(task.completedDate)}</span>
              </div>
            )}
            {task.statusUpdatedDate && (
              <div className="td-field">
                <label>Status Updated</label>
                <span>{fmtDate(task.statusUpdatedDate)}</span>
              </div>
            )}
          </div>

          {/* Remark */}
          {task.remark && (
            <div className="td-section">
              <label>Remark</label>
              <p className="td-desc">{task.remark}</p>
            </div>
          )}

          {/* Completion notes */}
          {task.completionNotes && (
            <div className="td-section">
              <label>Completion Notes</label>
              <p className="td-desc">{task.completionNotes}</p>
            </div>
          )}

          {/* Notes */}
          {task.notes && (
            <div className="td-section">
              <label>Notes</label>
              <p className="td-desc">{task.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
